import { Button, Checkbox, Modal, Select, Textarea, TextInput } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import Loader from '../Common/Loader';
import { Event } from '../../Models/Event';
import { formatDateInputs } from '../../Utils';
import EventApi from '../../Api/EventApi';
import { toast } from 'react-toastify';
import QRScanner from './QRScanner/QRScanner';
import { MdOutlinePersonAddAlt } from 'react-icons/md';
import SitePageApi from '../../Api/SitePageApi';
import { SitePage } from '../../Models/SitePage';
import { trackEvent } from '../../Utils/analytics';
import ContactSessionEditor, { ContactSessionEditorHandle } from './ContactSessionEditor';
import { TIME_ZONE_CHOICES, zoneDisplayName } from '../../Utils/timeZone';

export default function AddEditEventModal({
  show,
  setShow,
  selectedEvent,
  setselectedEvent,
  getEvents,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedEvent: Event | null;
  setselectedEvent: React.Dispatch<React.SetStateAction<Event | null>>;
  getEvents: () => Promise<void> | undefined;
}) {
  const [showQRScanner, setshowQRScanner] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [conferences, setConferences] = useState<Event[]>([]);
  const [sitePages, setSitePages] = useState<SitePage[]>([]);
  const contactSessionRef = useRef<ContactSessionEditorHandle>(null);
  const [formData, setFormData] = useState<Event>({
    id: 0,
    name: null,
    eventDate: null,
    description: null,
    dateCreated: null,
    isGlobal: false,
    location: null,
    timeZoneId: 'America/New_York',
    eventTime: null,
    eventEndTime: null,
    eventType: null,
    parentEventId: null,
    year: null,
    season: null,
    sitePageId: null,
    participationType: null,
    parentEventName: null,
    parentEventSeason: null,
    parentEventYear: null,
    notifyRegistrants: true, // Default to true - send notifications
    reminderMessage: null,
    enableReminder24h: true,
    enableReminder1h: true,
    enableReminder15m: true,
    eventParticipants: [],
  });

  const PARTICIPATION_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: '-- Select participation type --' },
    { value: 'BusinessMembers', label: 'Business members' },
    { value: 'SocialGuests', label: 'Social guests' },
    { value: 'AllAttendees', label: 'All attendees' },
    { value: 'BoardMembersOnly', label: 'Board members only' },
    { value: 'PastPresidentsAndBoardOnly', label: 'Past presidents and board members only' },
    { value: 'MentorsAndNewMembersOnly', label: 'Mentors and new members only' },
  ];

  // Convert TimeSpan string (HH:mm:ss or HH:mm) to HH:mm format for time input
  const formatTimeForInput = (timeString: string | null | undefined): string => {
    if (!timeString) return '';
    // Handle TimeSpan format from API (e.g., "14:30:00" or "14:30")
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`; // Return HH:mm
    }
    return timeString;
  };

  const handleSetInitialValues = () => {
    const values = selectedEvent
      ? {
          ...selectedEvent,
          eventTime: formatTimeForInput(selectedEvent.eventTime), // Convert TimeSpan to HH:mm
          eventEndTime: formatTimeForInput(selectedEvent.eventEndTime ?? null),
          notifyRegistrants: selectedEvent.notifyRegistrants !== undefined 
            ? selectedEvent.notifyRegistrants 
            : true, // Default to true if not set
          reminderMessage: selectedEvent.reminderMessage ?? null,
          enableReminder24h: selectedEvent.enableReminder24h !== false,
          enableReminder1h: selectedEvent.enableReminder1h !== false,
          enableReminder15m: selectedEvent.enableReminder15m !== false,
          timeZoneId: selectedEvent.timeZoneId ?? 'America/New_York',
        }
      : {
          id: 0,
          name: null,
          eventDate: null,
          description: null,
          dateCreated: null,
          isGlobal: false,
          location: null,
          timeZoneId: 'America/New_York',
          eventTime: null,
          eventEndTime: null,
          eventType: null,
          parentEventId: null,
          year: null,
          season: null,
          sitePageId: null,
          participationType: null,
          parentEventName: null,
          parentEventSeason: null,
          parentEventYear: null,
          notifyRegistrants: true, // Default to true for new events
          reminderMessage: null,
          enableReminder24h: true,
          enableReminder1h: true,
          enableReminder15m: true,
          eventParticipants: [],
        };
    setFormData(values as Event);
  };

  useEffect(() => {
    if (show) {
      handleSetInitialValues();
      loadConferences();
      loadSitePages();
    }
  }, [show]);

  const loadSitePages = async () => {
    try {
      const response = await SitePageApi.getSitePageList();
      if (response.data) {
        setSitePages(response.data);
      }
    } catch (error) {
      console.error('Failed to load site pages:', error);
    }
  };

  const loadConferences = async () => {
    try {
      // Load all conferences, not just current year
      const response = await EventApi.getAllConferences();
      if (response.data) {
        setConferences(response.data);
      }
    } catch (error) {
      console.error('Failed to load conferences:', error);
      // Fallback to current year if getAllConferences fails
      try {
        const currentYear = new Date().getFullYear();
        const [springRes, fallRes] = await Promise.all([
          EventApi.getCurrentConference('Spring', currentYear).catch(() => null),
          EventApi.getCurrentConference('Fall', currentYear).catch(() => null)
        ]);
        const confs: Event[] = [];
        if (springRes?.data) confs.push(springRes.data);
        if (fallRes?.data) confs.push(fallRes.data);
        setConferences(confs);
      } catch (fallbackError) {
        console.error('Failed to load conferences (fallback):', fallbackError);
      }
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prevData) => ({ ...prevData, [name]: checked }));
    } else {
      const updates: Partial<Event> = { [name]: value };
      
    // Auto-populate year and season when eventType changes
    if (name === 'eventType') {
      if (value === 'SpringConference') {
        updates.season = 'Spring';
        if (formData.eventDate) {
          const date = new Date(formData.eventDate);
          updates.year = date.getFullYear();
        }
      } else if (value === 'FallConference') {
        updates.season = 'Fall';
        if (formData.eventDate) {
          const date = new Date(formData.eventDate);
          updates.year = date.getFullYear();
        }
      } else if (value === 'Conference' && formData.eventDate) {
        const date = new Date(formData.eventDate);
        updates.year = date.getFullYear();
        updates.season = date.getMonth() < 6 ? 'Spring' : 'Fall';
      }
    }
    
    // Auto-populate year and season when eventDate changes and eventType is Conference
    if (name === 'eventDate' && (formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference') && value) {
      const date = new Date(value);
      updates.year = date.getFullYear();
      if (formData.eventType === 'SpringConference') {
        updates.season = 'Spring';
      } else if (formData.eventType === 'FallConference') {
        updates.season = 'Fall';
      } else {
        updates.season = date.getMonth() < 6 ? 'Spring' : 'Fall';
      }
    }
      
      // Handle year as number
      if (name === 'year') {
        updates.year = value ? Number(value) : null;
      }
      
      setFormData((prevData) => ({ ...prevData, ...updates }));
    }
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updates: Partial<Event> = { [name]: value === '' ? null : (name === 'parentEventId' || name === 'year' || name === 'sitePageId' ? Number(value) : value) };
    
    // Auto-populate year and season when eventType changes
    if (name === 'eventType') {
      if (value === 'SpringConference') {
        updates.season = 'Spring';
        if (formData.eventDate) {
          const date = new Date(formData.eventDate);
          updates.year = date.getFullYear();
        }
      } else if (value === 'FallConference') {
        updates.season = 'Fall';
        if (formData.eventDate) {
          const date = new Date(formData.eventDate);
          updates.year = date.getFullYear();
        }
      } else if (value === 'Conference' && formData.eventDate) {
        const date = new Date(formData.eventDate);
        updates.year = date.getFullYear();
        updates.season = date.getMonth() < 6 ? 'Spring' : 'Fall';
      }
    }
    
    // Auto-populate year and season when eventDate changes and eventType is Conference
    if (name === 'eventDate' && (formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference') && value) {
      const date = new Date(value);
      updates.year = date.getFullYear();
      if (formData.eventType === 'SpringConference') {
        updates.season = 'Spring';
      } else if (formData.eventType === 'FallConference') {
        updates.season = 'Fall';
      } else {
        updates.season = date.getMonth() < 6 ? 'Spring' : 'Fall';
      }
    }

    setFormData((prevData) => ({ ...prevData, ...updates }));
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const validateEvent = () => {
    if (formData.isGlobal && formData.eventType !== 'Conference' && formData.eventType !== 'SpringConference' && formData.eventType !== 'FallConference') {
      if (!formData.name || !formData.name.trim()) {
        toast.error('Name is required for global meetings.');
        return false;
      }
      if (!formData.eventDate) {
        toast.error('Date is required for global meetings.');
        return false;
      }
      if (!formData.eventTime) {
        toast.error('Time is required for global meetings.');
        return false;
      }
      if (!formData.location || !formData.location.trim()) {
        toast.error('Location is required for global meetings.');
        return false;
      }
    }
    return true;
  };

  const eventPayload = (
    notifyRegistrants: boolean | null | undefined,
    eventType?: string | null
  ): Event => {
    const toTimeSpan = (t: string | null | undefined) =>
      t ? (t.includes(':') && t.split(':').length === 2 ? `${t}:00` : t) : null;
    const { eventParticipants, ...eventData } = formData;
    return {
      ...eventData,
      eventType: eventType ?? formData.eventType,
      eventTime: toTimeSpan(formData.eventTime),
      eventEndTime: toTimeSpan(formData.eventEndTime ?? null),
      eventParticipants: [],
      notifyRegistrants,
    };
  };

  const persistEvent = async (
    notifyRegistrants: boolean | null | undefined,
    eventType?: string | null
  ) => {
    const res = await EventApi.addUpdateEvent(eventPayload(notifyRegistrants, eventType));
    if (!res.data.success) {
      throw new Error(res.data.message || 'Could not save the event.');
    }
    const id = Number(res.data.affectedEntityIdentifier) || formData.id;
    if (!id) {
      throw new Error('The event was saved but no id came back.');
    }
    setFormData((prev) => ({ ...prev, id, eventType: eventType ?? prev.eventType }));
    setselectedEvent((prev) => ({
      ...(prev ?? formData),
      ...formData,
      id,
      eventType: eventType ?? formData.eventType,
    }));
    getEvents();
    return id;
  };

  const ensureContactSessionSaved = async () => {
    if (!validateEvent()) {
      const err = new Error("validation");
      err.name = "ValidationError";
      throw err;
    }
    return persistEvent(false, 'ContactSession');
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    if (!validateEvent()) {
      setButtonDisabled(false);
      return;
    }

    try {
      const id = await persistEvent(formData.notifyRegistrants);
      if (formData.eventType === 'ContactSession') {
        await contactSessionRef.current?.saveDraft(id);
      }
      toast.success('Success');
      trackEvent(selectedEvent ? 'event_updated' : 'event_created', {
        event_id: id,
        event_type: formData.eventType ?? 'RegularEvent',
        is_global: !!formData.isGlobal,
      });
      setIsLoading(false);
      setButtonDisabled(false);
      onCloseModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error: ' + err);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  const isSubEvent = formData.eventType === 'SubEvent';

  const handleSendReminderNow = async () => {
    if (!selectedEvent) return;
    const shouldSend = window.confirm(
      "Send the reminder immediately to this event's participants?"
    );
    if (!shouldSend) return;

    try {
      setButtonDisabled(true);
      const res = await EventApi.sendReminderNow(selectedEvent.id);
      if (res.data.success) {
        toast.success(res.data.message || "Reminder sent.");
      } else {
        toast.error(res.data.message || "Failed to send reminder.");
      }
    } catch (err) {
      toast.error("Failed to send reminder.");
      console.error(err);
    } finally {
      setButtonDisabled(false);
    }
  };

  function onCloseModal() {
    const values = {
      id: 0,
      name: null,
      eventDate: null,
      description: null,
      dateCreated: null,
      isGlobal: false,
      location: null,
      eventTime: null,
      eventEndTime: null,
      eventType: null,
      parentEventId: null,
      year: null,
      season: null,
      sitePageId: null,
      participationType: null,
      parentEventName: null,
      parentEventSeason: null,
      parentEventYear: null,
      notifyRegistrants: true,
      reminderMessage: null,
      enableReminder24h: true,
      enableReminder1h: true,
      enableReminder15m: true,
      eventParticipants: [],
    };

    setFormData(values as Event);
    setShow(false);
    setselectedEvent(null);
  }
  const handleAddUserSave = () => {
    getEvents();
    setshowQRScanner(false);
  };
  const qrScanner =
    showQRScanner && selectedEvent ? (
      <QRScanner
        selectedEvent={selectedEvent}
        handleAddUserSave={handleAddUserSave}
      />
    ) : null;
  return (
    <>
      <Modal show={show} size='4xl' onClose={onCloseModal} popup>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <div className='w-full'>
            <div className='w-full'>
              <div className='mx-1 flex items-end justify-center'>
                <div className='flex flex-1 justify-center'>
                  <h1 className='text-gray-900 font-bold text-4xl'>
                    {selectedEvent ? 'Edit' : 'Add'} Event
                  </h1>
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit}>
              <div className='text-gray-500'>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Name:</div>
                  </div>
                  <div className='flex'>
                    <div className='w-full'>
                      <TextInput
                        id='name'
                        name='name'
                        type='text'
                        onChange={handleChangeInput}
                        value={formData.name ?? ''}
                        sizing='md'
                        required
                      />
                    </div>
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Event Date:</div>
                  </div>
                  <div className='w-full'>
                    <TextInput
                      id='eventDate'
                      name='eventDate'
                      type='date'
                      onChange={handleChangeInput}
                      value={formatDateInputs(formData.eventDate ?? '')}
                      sizing='md'
                      pattern='\d{4}-\d{2}-\d{2}'
                      required={formData.isGlobal === true}
                    />
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Event Time:</div>
                  </div>
                  <div className='w-full'>
                    <TextInput
                      id='eventTime'
                      name='eventTime'
                      type='time'
                      onChange={handleChangeInput}
                      value={formData.eventTime ?? ''}
                      sizing='md'
                      required={formData.isGlobal === true && formData.eventType !== 'Conference' && formData.eventType !== 'SpringConference' && formData.eventType !== 'FallConference'}
                      disabled={formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference'}
                      placeholder={formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference' ? 'Not required for conferences' : ''}
                    />
                    {(formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference') && (
                      <p className='text-sm text-gray-500 mt-1'>
                        Conferences span multiple days - time is optional
                      </p>
                    )}
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Event End Time:</div>
                  </div>
                  <div className='w-full'>
                    <TextInput
                      id='eventEndTime'
                      name='eventEndTime'
                      type='time'
                      onChange={handleChangeInput}
                      value={formData.eventEndTime ?? ''}
                      sizing='md'
                      disabled={formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference'}
                      placeholder={formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference' ? 'Optional' : 'Optional'}
                    />
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Location:</div>
                  </div>
                  <div className='w-full'>
                    <TextInput
                      id='location'
                      name='location'
                      type='text'
                      onChange={handleChangeInput}
                      value={formData.location ?? ''}
                      sizing='md'
                      required={formData.isGlobal === true}
                    />
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Event Type:</div>
                  </div>
                  <div className='w-full'>
                    <Select
                      id='eventType'
                      name='eventType'
                      onChange={handleChangeSelect}
                      value={formData.eventType ?? ''}
                      sizing='md'
                    >
                      <option value=''>Regular Event</option>
                      <option value='Conference'>Conference (Legacy)</option>
                      <option value='SpringConference'>Spring Conference</option>
                      <option value='FallConference'>Fall Conference</option>
                      <option value='SubEvent'>Sub-Event</option>
                      <option value='ContactSession'>Contact Session</option>
                      </Select>
                  </div>
                </div>
                {(formData.eventType === 'Conference' || formData.eventType === 'SpringConference' || formData.eventType === 'FallConference') && (
                  <>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Timezone:</div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='timeZoneId'
                          name='timeZoneId'
                          onChange={handleChangeSelect}
                          value={formData.timeZoneId ?? 'America/New_York'}
                          sizing='md'
                        >
                          {TIME_ZONE_CHOICES.map((zone) => (
                            <option key={zone.id} value={zone.id}>{zone.name}</option>
                          ))}
                        </Select>
                        <p className='text-sm text-gray-500 mt-1'>
                          Session and meeting times are the clock time in this timezone. Changing it keeps those clock times and updates reminders.
                        </p>
                      </div>
                    </div>
                  </>
                )}
                <hr className='bg-gray-300 h-[2px]'></hr>
                {(formData.eventType === 'SubEvent' || formData.eventType === 'ContactSession') && (
                  <>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Parent Conference:</div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='parentEventId'
                          name='parentEventId'
                          onChange={handleChangeSelect}
                          value={formData.parentEventId ?? ''}
                          sizing='md'
                        >
                          <option value=''>-- Select Conference --</option>
                          {conferences.map((conf) => {
                            const yearSeason = conf.season && conf.year 
                              ? `${conf.season} ${conf.year}`
                              : conf.year 
                                ? `${conf.year}`
                                : '';
                            const displayName = yearSeason 
                              ? `${conf.name} (${yearSeason})`
                              : conf.name || `Conference #${conf.id}`;
                            return (
                              <option key={conf.id} value={conf.id}>
                                {displayName}
                              </option>
                            );
                          })}
                        </Select>
                        <p className='text-sm text-gray-500 mt-1'>
                          Times use {zoneDisplayName(
                            conferences.find((c) => c.id === Number(formData.parentEventId))?.timeZoneId
                              ?? formData.timeZoneId
                          )} from the conference.
                        </p>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                  </>
                )}
                {formData.eventType === 'ContactSession' && (
                  <ContactSessionEditor
                    ref={contactSessionRef}
                    eventId={formData.id}
                    ensureEventId={ensureContactSessionSaved}
                  />
                )}
                {formData.eventType === 'Conference' && (
                  <>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Year:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='year'
                          name='year'
                          type='number'
                          onChange={handleChangeInput}
                          value={formData.year ?? ''}
                          sizing='md'
                          placeholder='e.g., 2027'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Season:</div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='season'
                          name='season'
                          onChange={handleChangeSelect}
                          value={formData.season ?? ''}
                          sizing='md'
                        >
                          <option value=''>-- Select Season --</option>
                          <option value='Spring'>Spring</option>
                          <option value='Fall'>Fall</option>
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                  </>
                )}
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Info Page:</div>
                  </div>
                  <div className='w-full'>
                    <Select
                      id='sitePageId'
                      name='sitePageId'
                      onChange={handleChangeSelect}
                      value={formData.sitePageId ?? ''}
                      sizing='md'
                    >
                      <option value=''>-- No Page --</option>
                      {sitePages.map((page) => (
                        <option key={page.id} value={page.id}>
                          {page.title || `Page #${page.id}`}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Participation Type:</div>
                  </div>
                  <div className='w-full'>
                    <Select
                      id='participationType'
                      name='participationType'
                      onChange={handleChangeSelect}
                      value={formData.participationType ?? ''}
                      sizing='md'
                    >
                      {PARTICIPATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value || 'none'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    <p className='text-sm text-gray-500 mt-1'>
                      Helps users know which global events they can participate in
                    </p>
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Global Meeting:</div>
                  </div>
                  <div className='flex items-center'>
                    <Checkbox
                      id='isGlobal'
                      name='isGlobal'
                      checked={formData.isGlobal === true}
                      onChange={handleChangeInput}
                    />
                    <label htmlFor='isGlobal' className='ml-2 text-sm'>
                      Show on everyone's schedule (no acceptance required)
                    </label>
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Reminder Message:</div>
                  </div>
                  <div className='w-full'>
                    <Textarea
                      id='reminderMessage'
                      name='reminderMessage'
                      value={formData.reminderMessage ?? ''}
                      onChange={handleChangeTextArea}
                      rows={4}
                    />
                    <p className='text-sm text-gray-500 mt-1'>
                      {isSubEvent
                        ? 'This message appears above the participant list on all reminder emails. If left blank, the system uses the default reminder text.'
                        : 'This message appears above the participant list on manual reminder emails. Top-level events do not send automatic reminders. If left blank, the system uses the default reminder text.'}
                    </p>
                  </div>
                </div>
                <hr className='bg-gray-300 h-[2px]'></hr>
                {isSubEvent && (
                  <>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Auto Reminders:</div>
                      </div>
                      <div className='space-y-3 py-2'>
                        <div className='flex items-center'>
                          <Checkbox
                            id='enableReminder24h'
                            name='enableReminder24h'
                            checked={formData.enableReminder24h !== false}
                            onChange={handleChangeInput}
                          />
                          <label htmlFor='enableReminder24h' className='ml-2 text-sm'>
                            Send 24 hours before the event
                          </label>
                        </div>
                        <div className='flex items-center'>
                          <Checkbox
                            id='enableReminder1h'
                            name='enableReminder1h'
                            checked={formData.enableReminder1h !== false}
                            onChange={handleChangeInput}
                          />
                          <label htmlFor='enableReminder1h' className='ml-2 text-sm'>
                            Send 1 hour before the event
                          </label>
                        </div>
                        <div className='flex items-center'>
                          <Checkbox
                            id='enableReminder15m'
                            name='enableReminder15m'
                            checked={formData.enableReminder15m !== false}
                            onChange={handleChangeInput}
                          />
                          <label htmlFor='enableReminder15m' className='ml-2 text-sm'>
                            Send 15 minutes before the event
                          </label>
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                  </>
                )}
                {selectedEvent && (
                  <>
                    <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Notify Registrants:</div>
                      </div>
                      <div className='flex items-center'>
                        <Checkbox
                          id='notifyRegistrants'
                          name='notifyRegistrants'
                          checked={formData.notifyRegistrants !== false}
                          onChange={handleChangeInput}
                        />
                        <label htmlFor='notifyRegistrants' className='ml-2 text-sm'>
                          Send email and push notifications to event registrants about this update
                        </label>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                  </>
                )}
                <div className='flex flex-col md:grid md:grid-cols-[20%_80%] text-xl'>
                  <div className='flex'>
                    <div className='font-bold content-center'>Description:</div>
                  </div>
                  <div className=''>
                    <Textarea
                      id='description'
                      name='description'
                      onChange={handleChangeTextArea}
                      value={formData.description ?? ''}
                      rows={4}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`flex ${
                  selectedEvent ? 'justify-between' : 'justify-end'
                }  pt-2`}
              >
                {selectedEvent && (
                  <div className='flex gap-2'>
                    <Button
                      disabled={buttonDisabled}
                      type='button'
                      className='buttonMainBlue'
                      onClick={() => setshowQRScanner(true)}
                    >
                      <MdOutlinePersonAddAlt size={20} />{' '}
                      <div className='pl-1'>Add User</div>
                    </Button>
                    <Button
                      disabled={buttonDisabled}
                      type='button'
                      className='buttonMain'
                      onClick={handleSendReminderNow}
                    >
                      Send Reminder Now
                    </Button>
                  </div>
                )}
                <Button
                  disabled={buttonDisabled}
                  type='submit'
                  className='buttonMain'
                >
                  Save
                </Button>
              </div>
            </form>
          </div>
          {isLoading && <Loader />}
        </Modal.Body>
      </Modal>
      <Modal show={showQRScanner} size='md' onClose={handleAddUserSave} popup>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <div>{qrScanner}</div>
        </Modal.Body>
      </Modal>
    </>
  );
}
